// Label model over the lossless XML tree. Edits touch only the nodes they target;
// everything else (including unsupported fields) is preserved byte-for-byte.
// Units are file units (HIMETRIC, 1/100 mm, integers). Pure module: no DOM.

import { decodeItf, encodeItf } from './encoding.js';
import {
  parseXml, serializeXml, XmlError, child, childrenOf, at, textOf, setText, elem, insertOrdered, removeNode, cloneNode,
  findIllegalChar, describeChar,
} from './xml.js';
import {
  ORDER, ALL_RENDER_STYLES, ALL_DATA_SOURCES, RENDER_STYLES, RENDER_STYLE_INFO, INK_COLORS, FIELD_ROTATIONS,
  HUMAN_READABLE, CODE128_CODE_SETS, DATAMATRIX_ECC, QR_ECC, OVERLAY_MODES, OFFSET_PROMPTING, LANGUAGES, NAME_PREFIX,
  DEVICES, textFieldTemplate, dateFieldTemplate, barcodeFieldTemplate, barcodeRenderStyleTemplate, blankLegendTemplate,
  staticSourceTemplate,
} from './schema.js';
import { CELL_TO_EM, defaultCharWidth } from '../render/metrics.js';

export class LabelError extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = 'LabelError';
  }
}

/** Integer range of every number stored in a label (CoLOS reads them as Int32). */
export const INT32_MIN = -2147483648;
export const INT32_MAX = 2147483647;

const RS_SET = new Set(ALL_RENDER_STYLES);
const DS_SET = new Set(ALL_DATA_SOURCES);
const BARCODE_SET = new Set(RENDER_STYLES.barcode);
const INT_RE = /^\s*[-+]?\d+\s*$/;
const BOOL_TEXT_RE = /^\s*(true|false|1|0)\s*$/;

const def = (v) => v !== undefined && v !== null;
const hasOwn = (obj, k) => Object.prototype.hasOwnProperty.call(obj, k);

// ------------------------------------------------------------------ reading helpers (lenient: files are data)

const intOf = (el) => {
  const t = textOf(el);
  return INT_RE.test(t) ? parseInt(t, 10) : 0;
};
const parseBool = (t) => {
  const s = t.trim();
  return s === 'true' || s === '1';
};
const boolOf = (el) => parseBool(textOf(el));

// ------------------------------------------------------------------ input validation (strict; throw LabelError, never mutate)

/**
 * A whole number in [min, max] (default Int32). Accepts integer numbers and plain decimal-digit strings
 * ('12', ' -5 ', '+3'); rejects fractions, NaN/Infinity, '', hex, exponent notation and other types.
 */
function toInt(v, what, min = INT32_MIN, max = INT32_MAX) {
  let n = NaN;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string' && INT_RE.test(v)) n = Number(v.trim());
  if (!Number.isInteger(n)) throw new LabelError(`${what} must be a whole number.`);
  if (n < min) {
    throw new LabelError(min === 1 ? `${what} must be greater than 0.` : min === 0 ? `${what} cannot be negative.` : `${what} must be at least ${min}.`);
  }
  if (n > max) throw new LabelError(`${what} is too large (at most ${max}).`);
  return n === 0 ? 0 : n; // no -0
}

function toBool(v, what) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (s === 'true') return true;
    if (s === 'false') return false;
  }
  throw new LabelError(`${what} must be true or false.`);
}

/**
 * A string that can be stored in the XML file. `singleLine`: CoLOS NonEmptyString (pattern `.+`)
 * does not allow CR/LF — used for Static text and barcode data.
 */
function toText(v, what, singleLine = false) {
  if (typeof v !== 'string') throw new LabelError(`${what} must be a text value.`);
  const bad = findIllegalChar(v);
  if (bad !== -1) {
    const unit = v.charCodeAt(bad);
    const kind = unit >= 0xd800 && unit <= 0xdfff ? 'an incomplete (unpaired surrogate) character' : 'a control or non-character that cannot be stored in a label file';
    throw new LabelError(`${what} contains ${kind} (${describeChar(v, bad)}). Remove it and try again.`);
  }
  if (singleLine && /[\r\n]/.test(v)) throw new LabelError(`${what} cannot contain line breaks.`);
  return v;
}

function oneOf(v, list, what) {
  if (!list.includes(v)) throw new LabelError(`${what} must be one of: ${list.join(', ')}.`);
  return v;
}

function toObject(v, what) {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) throw new LabelError(`${what} must be given as an object.`);
  return v;
}

function toRotation(v) {
  let r = NaN;
  try {
    r = toInt(v, 'Rotation');
  } catch {
    // reported below with the rotation-specific message
  }
  if (!FIELD_ROTATIONS.includes(r)) throw new LabelError('Rotation must be 0, 90, 180 or 270 degrees.');
  return r;
}

function toSymbology(v) {
  if (BARCODE_SET.has(v)) return v;
  const allowed = `Barcode type must be one of: ${RENDER_STYLES.barcode.join(', ')}.`;
  if (typeof v === 'string' && RS_SET.has(v) && v !== 'Text' && v !== 'Hidden') {
    throw new LabelError(`${v} barcodes are not supported by this editor. ${allowed}`);
  }
  throw new LabelError(allowed);
}

// ------------------------------------------------------------------ writers (values already validated)

/**
 * Write a leaf value only if it differs. `same(currentText)` decides equality;
 * `missingEqual` = the value equals what a reader assumes when the node is absent.
 */
function writeLeaf(parent, name, order, str, same, missingEqual) {
  const el = child(parent, name);
  if (el) {
    if (same(textOf(el)) && el.children.every((c) => c.type === 'text')) return false;
    setText(el, str);
    return true;
  }
  if (missingEqual) return false;
  const node = elem(name);
  setText(node, str);
  insertOrdered(parent, node, order);
  return true;
}

function writeStr(parent, name, order, s, missingDefault) {
  return writeLeaf(parent, name, order, s, (t) => t === s, missingDefault !== undefined && s === missingDefault);
}

function writeInt(parent, name, order, n, missingDefault) {
  if (!Number.isInteger(n)) throw new LabelError(`${name} must be a whole number.`); // guard; callers pass validated ints
  return writeLeaf(parent, name, order, String(n), (t) => INT_RE.test(t) && parseInt(t, 10) === n,
    missingDefault !== undefined && n === missingDefault);
}

function writeBool(parent, name, order, bv, missingDefault) {
  return writeLeaf(parent, name, order, bv ? 'true' : 'false', (t) => parseBool(t) === bv,
    missingDefault !== undefined && bv === missingDefault);
}

// ------------------------------------------------------------------ Label

class Label {
  constructor(doc, encoding) {
    Object.defineProperty(this, '_keyOf', { value: new WeakMap() });
    Object.defineProperty(this, '_elOf', { value: new Map() });
    Object.defineProperty(this, '_seq', { value: 0, writable: true });
    this.doc = doc;
    this.encoding = encoding;
    this.revision = 0;
    this.root = doc.children.find((c) => c.type === 'element');
    for (const el of childrenOf(this.fieldList)) this._register(el);
  }

  get fieldList() {
    return child(this.root, 'FieldList');
  }

  get width() {
    return intOf(child(this.root, 'Width'));
  }

  set width(v) {
    const n = toInt(v, 'Label width', 1);
    if (writeInt(this.root, 'Width', ORDER.Legend, n)) this.revision++;
  }

  get height() {
    return intOf(child(this.root, 'Height'));
  }

  set height(v) {
    const n = toInt(v, 'Label height', 1);
    if (writeInt(this.root, 'Height', ORDER.Legend, n)) this.revision++;
  }

  get device() {
    const info = child(this.root, 'DeviceInfo');
    const uriAttr = info ? info.attrs.find((a) => a.name === 'DeviceURI') : null;
    return { uri: uriAttr ? uriAttr.value : '', name: textOf(child(info, 'DeviceName')) };
  }

  get header() {
    for (const c of this.doc.children) {
      if (c.type === 'comment') return c.value;
      if (c.type === 'element') break;
    }
    return null;
  }

  _register(el) {
    let key = this._keyOf.get(el);
    if (!key) {
      key = `f${++this._seq}`;
      this._keyOf.set(el, key);
      this._elOf.set(key, el);
    }
    return key;
  }

  _unregister(el) {
    const key = this._keyOf.get(el);
    if (key) {
      this._keyOf.delete(el);
      this._elOf.delete(key);
    }
  }

  _element(key) {
    const el = this._elOf.get(key);
    if (!el || !this.fieldList.children.includes(el)) throw new LabelError(`Field "${key}" does not exist in this label.`);
    return el;
  }
}

function makeLabel(doc, encoding) {
  const root = doc.children.find((c) => c.type === 'element');
  if (!root || root.name !== 'Legend') {
    throw new LabelError(`This XML file is not a CoLOS label (it starts with <${root ? root.name : '?'}>, expected <Legend>).`);
  }
  if (!child(root, 'FieldList')) throw new LabelError('This label file is incomplete: it has no field list.');
  for (const [name, what] of [['Width', 'width'], ['Height', 'height']]) {
    const el = child(root, name);
    if (!el) throw new LabelError(`This label file is incomplete: the label ${what} is missing.`);
    if (!INT_RE.test(textOf(el))) throw new LabelError(`This label file is damaged: the label ${what} is not a whole number.`);
  }
  return new Label(doc, encoding);
}

// ------------------------------------------------------------------ open / new / save

/** @returns {Label} */
export function openLabel(bytes) {
  let decoded;
  try {
    decoded = decodeItf(bytes);
  } catch (e) {
    const detail = e && e.message ? `: ${e.message}` : '';
    throw new LabelError(`This file could not be read${detail}.`, { cause: e });
  }
  const { text, encoding } = decoded;
  const first = text.search(/\S/);
  if (first === -1) throw new LabelError('This file is empty.');
  if (text[first] !== '<') throw new LabelError('This is not a CoLOS label file (expected UTF-16 XML).');
  let doc;
  try {
    doc = parseXml(text);
  } catch (e) {
    if (e instanceof XmlError) {
      throw new LabelError(`This label file is damaged and cannot be opened (${e.reason}, at character ${e.offset}).`, { cause: e });
    }
    throw e;
  }
  return makeLabel(doc, encoding);
}

/** Blank label for a printer (default SmartDate X65). */
export function newLabel(deviceKey = 'SDX65iw') {
  if (!hasOwn(DEVICES, deviceKey)) throw new LabelError(`Unknown printer "${deviceKey}".`);
  return makeLabel(blankLegendTemplate(deviceKey), 'utf16le');
}

/**
 * @returns {Uint8Array} UTF-16 LE with BOM
 * @throws {LabelError} if the document contains characters XML cannot store (only possible via direct tree edits)
 */
export function saveLabel(label) {
  let xml;
  try {
    xml = serializeXml(label.doc);
  } catch (e) {
    if (e instanceof XmlError) throw new LabelError(`This label cannot be saved: ${e.reason}.`, { cause: e });
    throw e;
  }
  return encodeItf(xml);
}

// ------------------------------------------------------------------ field analysis / reading

/** Classify a FieldList child. Returns { kind, renderStyle, dataSource, rs, ds }. */
export function analyzeField(el) {
  if (el.name !== 'AlphaNumeric') return { kind: 'unsupported', renderStyle: el.name, dataSource: null, rs: null, ds: null };
  let rs = null;
  let ds = null;
  for (const c of childrenOf(el)) {
    if (!rs) {
      if (RS_SET.has(c.name)) rs = c;
    } else if (!ds && DS_SET.has(c.name)) {
      ds = c;
    }
  }
  const out = { kind: 'unsupported', renderStyle: rs ? rs.name : null, dataSource: ds ? ds.name : null, rs, ds };
  if (!rs || !ds || !child(el, 'Geometry')) return out;
  if (rs.name === 'Text') {
    const plainFont = child(rs, 'Font') && !child(rs, 'LegacyFont') && !child(rs, 'Paragraph') && !child(rs, 'FitToBox');
    if (!plainFont) return out;
    if (ds.name === 'Static') out.kind = 'text';
    else if (ds.name === 'TimeDate') out.kind = 'date';
  } else if (BARCODE_SET.has(rs.name) && ds.name === 'Static') {
    out.kind = 'barcode';
  }
  return out;
}

// The printer's own files size a font with <PointSizeInHM> (em size, 1/100 mm) and pick bold through the
// font file (arialbd.ttf / arial.ttf) instead of CharHeight/CharWidth/Bold. Reading maps that onto the
// same model (height = cell height, normal width); writing keeps the printer's form.
const BOLD_FILE_RE = /(bd|bold)\.ttf$/i;

function readFont(fontEl) {
  const typeface = textOf(child(fontEl, 'Typeface'));
  const ps = child(fontEl, 'PointSizeInHM');
  if (ps && !child(fontEl, 'CharHeight')) {
    const pointSize = intOf(ps);
    const height = Math.round(pointSize * CELL_TO_EM);
    return {
      typeface,
      height,
      width: defaultCharWidth(typeface, height),
      bold: BOLD_FILE_RE.test(textOf(child(fontEl, 'Filename'))),
      italic: false,
      underline: false,
      rtl: false,
      pointSize,
      file: textOf(child(fontEl, 'Filename')),
    };
  }
  return {
    typeface,
    height: intOf(child(fontEl, 'CharHeight')),
    width: intOf(child(fontEl, 'CharWidth')),
    bold: boolOf(child(fontEl, 'Bold')),
    italic: boolOf(child(fontEl, 'Italic')),
    underline: boolOf(child(fontEl, 'Underline')),
    rtl: boolOf(child(fontEl, 'RightToLeftReadingOrder')),
  };
}

function normRotation(el) {
  const r = intOf(el);
  return ((r % 360) + 360) % 360;
}

function readField(label, el) {
  const a = analyzeField(el);
  const idText = textOf(child(el, 'ID'));
  const f = {
    key: label._register(el),
    kind: a.kind,
    name: textOf(child(el, 'Name')),
    id: INT_RE.test(idText) ? parseInt(idText, 10) : null,
    x: intOf(at(el, ['Geometry', 'X'])),
    y: intOf(at(el, ['Geometry', 'Y'])),
    rotation: normRotation(at(el, ['Geometry', 'Rotation'])),
    color: textOf(child(el, 'FieldColor')) || 'BLACK',
    locked: a.kind === 'unsupported',
  };
  const { rs, ds } = a;
  switch (a.kind) {
    case 'text':
      f.text = textOf(child(ds, 'Text'));
      f.font = readFont(child(rs, 'Font'));
      f.inverted = boolOf(child(rs, 'Inverted'));
      break;
    case 'date': {
      const rule = child(ds, 'OffsetRule');
      f.font = readFont(child(rs, 'Font'));
      f.inverted = boolOf(child(rs, 'Inverted'));
      f.format = textOf(child(ds, 'TDFormatString'));
      f.language = textOf(child(ds, 'LanguageValue'));
      f.offset = {
        days: intOf(child(rule, 'OffsetDays')),
        months: intOf(child(rule, 'OffsetMonths')),
        years: intOf(child(rule, 'OffsetYears')),
        hours: intOf(child(rule, 'OffsetHours')),
      };
      f.prompting = textOf(child(rule, 'PromptingBehaviour')) || 'DO_NOT_PROMPT';
      f.isFixed = boolOf(child(ds, 'IsFixed'));
      break;
    }
    case 'barcode': {
      const oneD = RENDER_STYLE_INFO[rs.name].family === 'oneD';
      f.symbology = rs.name;
      f.data = textOf(child(ds, 'Text'));
      f.inverted = boolOf(child(rs, 'Inverted'));
      f.barHeight = oneD ? intOf(child(rs, 'BarHeight')) : null;
      f.narrowBarWidth = oneD ? intOf(child(rs, 'NarrowBarWidth')) : null;
      f.quietMargin = boolOf(child(rs, 'QuietMargin'));
      f.autoCheckDigit = oneD ? boolOf(child(rs, 'AutoCheckDigit')) : null;
      f.textDisplay = oneD ? textOf(child(rs, 'TextDisplayOptions')) || null : null;
      if (ORDER[rs.name].includes('CodeSet')) f.codeSet = textOf(child(rs, 'CodeSet')) || 'Auto';
      if (!oneD) {
        f.cellWidth = intOf(child(rs, 'CellWidth'));
        f.errorCorrection = textOf(child(rs, 'ErrorCorrection'));
      }
      break;
    }
    default: {
      f.renderStyle = a.renderStyle;
      f.dataSource = a.dataSource;
      // Extras for drawing a placeholder (not part of the editable model).
      const font = rs && child(rs, 'Font');
      if (font) f.font = readFont(font);
      const sizeFrom = child(el, 'Width') ? el : rs && child(rs, 'Width') ? rs : null;
      if (sizeFrom) {
        f.width = intOf(child(sizeFrom, 'Width'));
        f.height = intOf(child(sizeFrom, 'Height'));
      }
    }
  }
  return f;
}

/** Fields in FieldList order (= z-order, last on top). */
export function readFields(label) {
  return childrenOf(label.fieldList).map((el) => readField(label, el));
}

export function getField(label, key) {
  return readField(label, label._element(key));
}

// ------------------------------------------------------------------ updating: phase 1 — validate the whole patch

function planFont(font) {
  const f = toObject(font, 'Font');
  const o = {};
  if (def(f.typeface)) o.typeface = toText(f.typeface, 'Font name');
  if (def(f.rtl)) o.rtl = toBool(f.rtl, 'Right-to-left reading order');
  if (def(f.height)) o.height = toInt(f.height, 'Font height', 1);
  if (def(f.width)) o.width = toInt(f.width, 'Font width', 1);
  if (def(f.bold)) o.bold = toBool(f.bold, 'Bold');
  if (def(f.italic)) o.italic = toBool(f.italic, 'Italic');
  if (def(f.underline)) o.underline = toBool(f.underline, 'Underline');
  return o;
}

const OFFSET_PARTS = [
  ['days', 'OffsetDays', 'Offset days'],
  ['months', 'OffsetMonths', 'Offset months'],
  ['years', 'OffsetYears', 'Offset years'],
  ['hours', 'OffsetHours', 'Offset hours'],
];

/**
 * Validate and normalise an updateField patch for a field (analyzeField result) without touching the tree.
 * Keys that do not apply to the field's kind (or to the final barcode symbology) are ignored, as before.
 * @returns {object} normalised values
 */
function planPatch(a, patch) {
  const p = def(patch) ? toObject(patch, 'Field changes') : {};
  const v = {};
  if (def(p.name)) v.name = toText(p.name, 'Field name');
  if (def(p.id)) v.id = toInt(p.id, 'Field ID', 0);
  if (def(p.x)) v.x = toInt(p.x, 'X position');
  if (def(p.y)) v.y = toInt(p.y, 'Y position');
  if (def(p.rotation)) v.rotation = toRotation(p.rotation);
  if (def(p.color)) v.color = oneOf(p.color, INK_COLORS, 'Colour');

  if (a.kind === 'text' || a.kind === 'date') {
    if (def(p.font)) v.font = planFont(p.font);
    if (def(p.inverted)) v.inverted = toBool(p.inverted, 'Inverted');
  }

  if (a.kind === 'text') {
    if (def(p.text)) v.text = toText(p.text, 'Text', true);
  } else if (a.kind === 'date') {
    if (def(p.format)) v.format = toText(p.format, 'Date format');
    if (def(p.language)) v.language = oneOf(p.language, LANGUAGES, 'Date language');
    if (def(p.offset)) {
      const o = toObject(p.offset, 'Date offset');
      v.offset = {};
      for (const [k, name, what] of OFFSET_PARTS) if (def(o[k])) v.offset[name] = toInt(o[k], what);
    }
    if (def(p.prompting)) v.prompting = oneOf(p.prompting, OFFSET_PROMPTING, 'Offset prompting');
    if (def(p.isFixed)) v.isFixed = toBool(p.isFixed, 'Fixed date');
  } else if (a.kind === 'barcode') {
    const sym = def(p.symbology) ? toSymbology(p.symbology) : a.rs.name;
    v.symbology = sym;
    const oneD = RENDER_STYLE_INFO[sym].family === 'oneD';
    if (def(p.data)) v.data = toText(p.data, 'Barcode data', true);
    if (def(p.inverted)) v.inverted = toBool(p.inverted, 'Inverted');
    if (def(p.quietMargin)) v.quietMargin = toBool(p.quietMargin, 'Quiet zone');
    if (oneD) {
      if (def(p.barHeight)) v.barHeight = toInt(p.barHeight, 'Bar height', 1);
      if (def(p.narrowBarWidth)) v.narrowBarWidth = toInt(p.narrowBarWidth, 'Narrow bar width', 1);
      if (def(p.autoCheckDigit)) v.autoCheckDigit = toBool(p.autoCheckDigit, 'Automatic check digit');
      if (def(p.textDisplay)) v.textDisplay = oneOf(p.textDisplay, HUMAN_READABLE, 'Barcode text display');
      if (def(p.codeSet) && ORDER[sym].includes('CodeSet')) v.codeSet = oneOf(p.codeSet, CODE128_CODE_SETS, 'Code 128 code set');
    } else {
      if (def(p.cellWidth)) v.cellWidth = toInt(p.cellWidth, 'Cell width', 1);
      if (def(p.errorCorrection)) {
        v.errorCorrection = sym === 'QR'
          ? oneOf(p.errorCorrection, QR_ECC, 'QR error correction')
          : oneOf(p.errorCorrection, DATAMATRIX_ECC, 'Data Matrix error correction');
      }
    }
  }
  return v;
}

// ------------------------------------------------------------------ updating: phase 2 — write

function writeFont(rs, font) {
  const fontEl = child(rs, 'Font');
  const O = ORDER.Font;
  let c = false;
  if (child(fontEl, 'PointSizeInHM') && !child(fontEl, 'CharHeight')) {
    // printer form: only typeface, size and bold apply
    if (font.typeface !== undefined) c = writeStr(fontEl, 'Typeface', O, font.typeface) || c;
    if (font.height !== undefined) c = writeInt(fontEl, 'PointSizeInHM', O, Math.max(1, Math.round(font.height / CELL_TO_EM))) || c;
    if (font.bold !== undefined) {
      const cur = textOf(child(fontEl, 'Filename'));
      if (!cur || /^arial(bd)?\.ttf$/i.test(cur)) c = writeStr(fontEl, 'Filename', O, font.bold ? 'arialbd.ttf' : 'arial.ttf') || c;
    }
    return c;
  }
  if (font.typeface !== undefined) c = writeStr(fontEl, 'Typeface', O, font.typeface) || c;
  if (font.rtl !== undefined) c = writeBool(fontEl, 'RightToLeftReadingOrder', O, font.rtl, false) || c;
  if (font.height !== undefined) c = writeInt(fontEl, 'CharHeight', O, font.height) || c;
  if (font.width !== undefined) c = writeInt(fontEl, 'CharWidth', O, font.width) || c;
  if (font.bold !== undefined) c = writeBool(fontEl, 'Bold', O, font.bold, false) || c;
  if (font.italic !== undefined) c = writeBool(fontEl, 'Italic', O, font.italic, false) || c;
  if (font.underline !== undefined) c = writeBool(fontEl, 'Underline', O, font.underline, false) || c;
  return c;
}

/**
 * Swap the barcode render style, carrying over every setting the new type also has
 * (when the old value is valid for the new type); everything else comes from the new type's defaults.
 */
function replaceRenderStyle(el, rs, symbology) {
  const next = barcodeRenderStyleTemplate(symbology);
  const isOneD = (name) => RENDER_STYLE_INFO[name].family === 'oneD';
  const carry = (name, normalize) => {
    const from = child(rs, name);
    const to = child(next, name);
    if (!from || !to || !from.children.every((c) => c.type === 'text')) return;
    const value = normalize(textOf(from));
    if (value !== null) setText(to, value);
  };
  const asBool = (t) => (BOOL_TEXT_RE.test(t) ? (parseBool(t) ? 'true' : 'false') : null);
  const asSize = (t) => {
    if (!INT_RE.test(t)) return null;
    const n = Number(t.trim());
    return n >= 1 && n <= INT32_MAX ? String(n) : null;
  };
  const asEnum = (list) => (t) => (list.includes(t) ? t : null);

  carry('Inverted', asBool);
  carry('OverlayMode', asEnum(OVERLAY_MODES));
  const oldApp = child(rs, 'AppInfo');
  const newApp = child(next, 'AppInfo');
  if (oldApp) next.children[next.children.indexOf(newApp)] = cloneNode(oldApp);
  else removeNode(next, newApp);
  carry('QuietMargin', asBool);
  if (isOneD(rs.name) && isOneD(symbology)) {
    carry('BarHeight', asSize);
    carry('NarrowBarWidth', asSize);
    carry('AutoCheckDigit', asBool);
    carry('TextDisplayOptions', asEnum(HUMAN_READABLE));
    carry('CodeSet', asEnum(CODE128_CODE_SETS));
  } else if (!isOneD(rs.name) && !isOneD(symbology)) {
    carry('CellWidth', asSize);
    // DataMatrix and QR ECC enums differ: an invalid value keeps the new type's default.
    carry('ErrorCorrection', asEnum(symbology === 'QR' ? QR_ECC : DATAMATRIX_ECC));
  }
  el.children[el.children.indexOf(rs)] = next;
  return next;
}

function ensureOffsetRule(ds) {
  let rule = child(ds, 'OffsetRule');
  if (!rule) {
    rule = elem('OffsetRule', {},
      elem('OffsetDays', {}, '0'), elem('OffsetMonths', {}, '0'), elem('OffsetYears', {}, '0'),
      elem('PromptingBehaviour', {}, 'DO_NOT_PROMPT'));
    insertOrdered(ds, rule, ORDER.TimeDate);
  }
  return rule;
}

/** Apply a validated plan. Returns true if anything was written. */
function applyPlan(el, a, v) {
  let changed = false;
  const W = (r) => { if (r) changed = true; };

  const AO = ORDER.AlphaNumeric;
  if (v.name !== undefined) W(writeStr(el, 'Name', AO, v.name));
  if (v.id !== undefined) W(writeInt(el, 'ID', AO, v.id));
  const geo = child(el, 'Geometry');
  if (v.x !== undefined) W(writeInt(geo, 'X', ORDER.Geometry, v.x));
  if (v.y !== undefined) W(writeInt(geo, 'Y', ORDER.Geometry, v.y));
  if (v.rotation !== undefined) W(writeInt(geo, 'Rotation', ORDER.Geometry, v.rotation));
  if (v.color !== undefined) W(writeStr(el, 'FieldColor', AO, v.color, 'BLACK'));

  let { rs } = a;
  const { ds } = a;
  if (a.kind === 'text' || a.kind === 'date') {
    if (v.font !== undefined) W(writeFont(rs, v.font));
    if (v.inverted !== undefined) W(writeBool(rs, 'Inverted', ORDER.Text, v.inverted, false));
  }

  if (a.kind === 'text') {
    if (v.text !== undefined) W(writeStr(ds, 'Text', ORDER.Static, v.text));
  } else if (a.kind === 'date') {
    const TO = ORDER.TimeDate;
    const RO = ORDER.OffsetRule;
    if (v.format !== undefined) W(writeStr(ds, 'TDFormatString', TO, v.format));
    if (v.language !== undefined) W(writeStr(ds, 'LanguageValue', TO, v.language));
    if (v.offset !== undefined) {
      const vals = v.offset;
      const nonZero = Object.values(vals).some((n) => n !== 0);
      const rule = child(ds, 'OffsetRule') || (nonZero ? ensureOffsetRule(ds) : null);
      if (rule) {
        for (const [name, n] of Object.entries(vals)) {
          W(writeInt(rule, name, RO, n, name === 'OffsetHours' || !nonZero ? 0 : undefined));
        }
      }
    }
    if (v.prompting !== undefined) {
      const rule = child(ds, 'OffsetRule') || (v.prompting !== 'DO_NOT_PROMPT' ? ensureOffsetRule(ds) : null);
      if (rule) W(writeStr(rule, 'PromptingBehaviour', RO, v.prompting));
    }
    if (v.isFixed !== undefined) W(writeBool(ds, 'IsFixed', TO, v.isFixed, false));
  } else if (a.kind === 'barcode') {
    if (v.symbology !== rs.name) {
      rs = replaceRenderStyle(el, rs, v.symbology);
      changed = true;
    }
    const RO = ORDER[rs.name];
    if (v.data !== undefined) W(writeStr(ds, 'Text', ORDER.Static, v.data));
    if (v.inverted !== undefined) W(writeBool(rs, 'Inverted', RO, v.inverted, false));
    if (v.quietMargin !== undefined) W(writeBool(rs, 'QuietMargin', RO, v.quietMargin));
    if (v.barHeight !== undefined) W(writeInt(rs, 'BarHeight', RO, v.barHeight));
    if (v.narrowBarWidth !== undefined) W(writeInt(rs, 'NarrowBarWidth', RO, v.narrowBarWidth));
    if (v.autoCheckDigit !== undefined) W(writeBool(rs, 'AutoCheckDigit', RO, v.autoCheckDigit));
    if (v.textDisplay !== undefined) W(writeStr(rs, 'TextDisplayOptions', RO, v.textDisplay));
    if (v.codeSet !== undefined) W(writeStr(rs, 'CodeSet', RO, v.codeSet));
    if (v.cellWidth !== undefined) W(writeInt(rs, 'CellWidth', RO, v.cellWidth));
    if (v.errorCorrection !== undefined) W(writeStr(rs, 'ErrorCorrection', RO, v.errorCorrection));
  }
  return changed;
}

/**
 * Shallow patch; `font` and `offset` sub-objects are merged. Writes only changed values.
 * Atomic: the whole patch is validated first; if anything is invalid a LabelError is thrown and
 * neither the XML nor `label.revision` changes.
 * @returns {object} the updated Field
 */
export function updateField(label, key, patch = {}) {
  const el = label._element(key);
  const a = analyzeField(el);
  if (a.kind === 'unsupported') {
    throw new LabelError('This field uses features the editor does not support, so it is read-only.');
  }
  const plan = planPatch(a, patch);
  const backup = cloneNode(el); // safety net: writes below should not throw, but never leave a half-applied edit
  let changed;
  try {
    changed = applyPlan(el, a, plan);
  } catch (e) {
    el.children = backup.children;
    el.selfClosing = backup.selfClosing;
    if (e instanceof LabelError) throw e;
    throw new LabelError('This change could not be applied; the field was left unchanged.', { cause: e });
  }
  if (changed) label.revision++;
  return readField(label, el);
}

/**
 * Turn a Date field into FIXED text, e.g. "26.08.2026". A date field can only say "printer clock + offset", so one
 * exact date is stored the way the real labels store any fixed text: a Static data source. Name, ID, position, font
 * and colour are kept; only the data source changes (TimeDate -> Static). Atomic: validated first, otherwise
 * LabelError and nothing changes. The field reads back as kind 'text'.
 * @returns {object} the updated Field
 */
export function convertDateToFixedText(label, key, text) {
  const el = label._element(key);
  const a = analyzeField(el);
  if (a.kind !== 'date') throw new LabelError('Only a date field can be turned into fixed text.');
  const value = toText(text, 'Fixed date text', true);
  if (!value.trim()) throw new LabelError('Fixed date text cannot be empty.');
  el.children[el.children.indexOf(a.ds)] = staticSourceTemplate(value);
  label.revision++;
  return readField(label, el);
}

// ------------------------------------------------------------------ add / remove / duplicate / move

function nextId(label) {
  let max = -1;
  for (const el of childrenOf(label.fieldList)) {
    const t = textOf(child(el, 'ID'));
    if (!INT_RE.test(t)) continue;
    const n = Number(t.trim());
    if (n <= INT32_MAX && n > max) max = n; // out-of-range IDs cannot collide with new ones
  }
  if (max >= INT32_MAX) throw new LabelError(`This label has no free field ID left (IDs cannot exceed ${INT32_MAX}).`);
  return max + 1;
}

function uniqueName(label, prefix) {
  const used = new Set(childrenOf(label.fieldList).map((el) => textOf(child(el, 'Name'))));
  if (!used.has(prefix)) return prefix;
  for (let n = 2; ; n++) if (!used.has(`${prefix}_${n}`)) return `${prefix}_${n}`;
}

function appendField(label, el, afterEl) {
  const fl = label.fieldList;
  const k = afterEl ? fl.children.indexOf(afterEl) : -1;
  if (k === -1) fl.children.push(el);
  else fl.children.splice(k + 1, 0, el);
  fl.selfClosing = false;
  return label._register(el);
}

/**
 * Add a new Text/Date/Barcode field on top. `init` is applied as an updateField patch.
 * Atomic: an invalid kind/init throws LabelError and leaves the label unchanged.
 */
export function addField(label, kind, init = {}) {
  if (!hasOwn(NAME_PREFIX, kind)) throw new LabelError(`Cannot add a field of type "${kind}".`);
  const prefix = NAME_PREFIX[kind];
  const { kind: _k, key: _key, locked: _l, ...patch } = def(init) ? toObject(init, 'New field settings') : {};
  const base = { name: uniqueName(label, prefix), id: nextId(label) };
  let el;
  if (kind === 'text') el = textFieldTemplate(base);
  else if (kind === 'date') el = dateFieldTemplate(base);
  else el = barcodeFieldTemplate({ ...base, symbology: def(patch.symbology) ? toSymbology(patch.symbology) : 'EAN13' });
  planPatch(analyzeField(el), patch); // validate before the label is touched

  const fl = label.fieldList;
  const wasSelfClosing = fl.selfClosing;
  const key = appendField(label, el);
  try {
    updateField(label, key, patch);
  } catch (e) {
    removeNode(fl, el);
    fl.selfClosing = wasSelfClosing;
    label._unregister(el);
    throw e;
  }
  label.revision++;
  return key;
}

export function removeField(label, key) {
  const el = label._element(key);
  removeNode(label.fieldList, el);
  label._unregister(el);
  label.revision++;
}

/** Copy a field (placed just above the original), offset by +250 units in x and y. */
export function duplicateField(label, key) {
  const src = label._element(key);
  const a = analyzeField(src);
  if (a.kind === 'unsupported') throw new LabelError('This field is read-only and cannot be duplicated.');
  const srcGeo = child(src, 'Geometry');
  const x = toInt(intOf(child(srcGeo, 'X')) + 250, 'X position');
  const y = toInt(intOf(child(srcGeo, 'Y')) + 250, 'Y position');
  const name = uniqueName(label, NAME_PREFIX[a.kind]);
  const id = nextId(label);
  const copy = cloneNode(src);
  setText(child(copy, 'Name'), name);
  setText(child(copy, 'ID'), String(id));
  const geo = child(copy, 'Geometry');
  writeInt(geo, 'X', ORDER.Geometry, x);
  writeInt(geo, 'Y', ORDER.Geometry, y);
  const newKey = appendField(label, copy, src);
  label.revision++;
  return newKey;
}

/** Move a field to `toIndex` within the FieldList (index among fields, clamped). */
export function moveField(label, key, toIndex) {
  const el = label._element(key);
  const fl = label.fieldList;
  const fields = childrenOf(fl);
  const from = fields.indexOf(el);
  const to = Math.max(0, Math.min(fields.length - 1, Math.trunc(Number(toIndex) || 0)));
  if (to === from) return;
  fl.children.splice(fl.children.indexOf(el), 1);
  const rest = childrenOf(fl);
  if (to >= rest.length) fl.children.splice(fl.children.indexOf(rest[rest.length - 1]) + 1, 0, el);
  else fl.children.splice(fl.children.indexOf(rest[to]), 0, el);
  label.revision++;
}

/** The XML element behind a field key (for advanced/read-only use, e.g. debugging). */
export function fieldElement(label, key) {
  return label._element(key);
}
